<template>
  <div>
    <HiraysHeader inner-page />
    <FloatingSidebar />
    <div class="main">
      <div
        v-if="showBanner"
        class="nyBanner"
        :style="bannerStyle"
      >
        <div class="wrap flexsta">
          <div class="breadcrumbDiv flexsta">
            <i class="font_family icon-shouye" aria-hidden="true"></i>
            <router-link to="/">{{ $t('common.home') }}</router-link>
            <template v-if="breadcrumb">
              <span class="breadcrumbDiv__sep">&nbsp;/&nbsp;</span>
              <router-link v-if="resolvedBreadcrumbTo" :to="resolvedBreadcrumbTo">{{ breadcrumb }}</router-link>
              <span v-else>{{ breadcrumb }}</span>
            </template>
            <template v-if="pageTitle">
              <span class="breadcrumbDiv__sep">&nbsp;/&nbsp;</span>
              <router-link v-if="pageTitleTo" :to="pageTitleTo">{{ pageTitle }}</router-link>
              <span v-else>{{ pageTitle }}</span>
            </template>
          </div>
        </div>
      </div>
      <slot></slot>
    </div>
    <HiraysFooter />
  </div>
</template>

<script>
import HiraysHeader from '@/components/hirays/Header.vue'
import HiraysFooter from '@/components/hirays/Footer.vue'
import FloatingSidebar from '@/components/hirays/FloatingSidebar.vue'
import { fetchSiteMedia, getPageBanner, resetSiteMediaCache } from '@/utils/siteMedia'
import { setPageTitle } from '@/utils/pageTitle'

export default {
  name: 'PageLayout',
  components: { HiraysHeader, HiraysFooter, FloatingSidebar },
  props: {
    pageTitle: { type: String, default: '' },
    breadcrumb: { type: String, default: '' },
    /** Parent crumb link, e.g. `/product/` for 产品中心 */
    breadcrumbTo: { type: String, default: '' },
    /** Optional link for the last crumb (current page usually stays plain text) */
    pageTitleTo: { type: String, default: '' },
    bannerKey: { type: String, default: 'product' },
    showBanner: { type: Boolean, default: true }
  },
  data() {
    return {
      bannerBg: getPageBanner(this.bannerKey)
    }
  },
  computed: {
    resolvedBreadcrumbTo() {
      if (this.breadcrumbTo) return this.breadcrumbTo
      if (!this.breadcrumb) return ''
      const byBanner = {
        product: '/product/',
        news: '/news/',
        solution: '/case/',
        about: '/about/',
        service: '/service/',
        contact: '/contact/'
      }
      return byBanner[this.bannerKey] || ''
    },
    bannerStyle() {
      const url = this.$media(this.bannerBg)
      const style = {
        backgroundColor: '#1f4396',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center center',
        backgroundSize: 'cover',
        minHeight: '24vw'
      }
      if (url) {
        style.backgroundImage = `url(${url})`
      }
      return style
    }
  },
  created() {
    this.loadBanner()
    this.syncDocumentTitle()
  },
  watch: {
    bannerKey() {
      this.loadBanner()
    },
    pageTitle() {
      this.syncDocumentTitle()
    },
    '$i18n.locale'() {
      this.syncDocumentTitle()
    }
  },
  mounted() {
    document.body.classList.add('on-fixed')
    document.body.classList.add('page-inner')
    document.body.classList.remove('page-home')
    this.cleanupOrphanChrome()
    this.$nextTick(() => {
      this.cleanupOrphanChrome()
      if (window.initNyPage) window.initNyPage()
      if (typeof WOW !== 'undefined') new WOW().init()
      // Late-arriving fullpage/swiper leftovers after SPA jumps from home
      window.setTimeout(() => this.cleanupOrphanChrome(), 120)
      window.setTimeout(() => this.cleanupOrphanChrome(), 600)
    })
  },
  beforeDestroy() {
    document.body.classList.remove('on-fixed')
    document.body.classList.remove('page-inner')
  },
  methods: {
    cleanupOrphanChrome() {
      if (typeof document === 'undefined') return
      const kill = (sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          try { el.parentNode && el.parentNode.removeChild(el) } catch (e) { /* ignore */ }
        })
      }
      kill('#fp-nav')
      kill('body > .swiper-pagination')
      kill('.main > .swiper-pagination')
      kill('.nyBanner .swiper-pagination')
      kill('.banner1 .swiper-pagination')
      // Home fullpage side dots must never sit on inner pages
      if (!document.body.classList.contains('page-home')) {
        kill('#menu')
      }
    },
    syncDocumentTitle() {
      if (this.pageTitle) setPageTitle(this.pageTitle)
    },
    reloadCmsData() {
      resetSiteMediaCache()
      this.loadBanner()
    },
    async loadBanner() {
      const media = await fetchSiteMedia(true, this.$i18n.locale)
      this.bannerBg = getPageBanner(this.bannerKey, media)
    }
  }
}
</script>
